/*
 * sc-unit-price.js
 *
 * Keeps the "price per 100g" line in the buy box in sync with the selected variant.
 *
 * Horizon does NOT re-render arbitrary blocks on a variant change: variant-picker.js
 * morphs only the <variant-picker> element itself. What it does do is fetch the whole
 * product page for the newly selected options, parse it into a Document, and hand that
 * Document to every listener of shopify:product:select via the event's promise.
 *
 * So there is no price maths here — we just lift our own freshly server-rendered markup
 * out of that Document. Same approach as Horizon's product-price.js / product-inventory.js.
 */

import { StandardEvents } from '@shopify/events';

class ScUnitPrice extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._scope = this.closest('.shopify-section, dialog');
    if (!this._scope) return;
    this._onSelect = this._onSelect.bind(this);
    this._scope.addEventListener(StandardEvents.productSelect, this._onSelect);
    this._bound = true;
  }

  disconnectedCallback() {
    if (!this._bound || !this._scope) return;
    this._scope.removeEventListener(StandardEvents.productSelect, this._onSelect);
    this._bound = false;
  }

  _onSelect(event) {
    const promise = event && event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    promise
      .then((result) => {
        const detail = result && result.detail;
        if (!detail || !detail.html) return;

        // Combined listings can swap to a different product; that path morphs <main>
        // and re-creates this element server-side, so leave it alone.
        if (detail.newProduct) return;

        const next = detail.html.querySelector(
          `sc-unit-price[data-block-id="${this.dataset.blockId}"]`
        );

        // No element in the new markup means the new variant's size could not be
        // parsed into grams. Hide rather than show a stale figure.
        if (!next) {
          this.hidden = true;
          return;
        }

        this.hidden = false;
        this.innerHTML = next.innerHTML;
      })
      .catch(() => {
        /* A failed variant fetch is handled by the picker; leave the last good value. */
      });
  }
}

if (!customElements.get('sc-unit-price')) {
  customElements.define('sc-unit-price', ScUnitPrice);
}
